import React, { useCallback } from 'react';
import { CurtainState, DeviceType } from 'app/model/Device';
import { CurtainSingleButton, CurtainStatePanel, LampButton, RelayButton } from 'app/home/main-pages/devices/Devices'
import { useLocalizedStrings } from 'app/localization/Context';

import './All.scss'

function Button({
    on, buttonOrder, onClickHandler, darkerBorder, waitingForChangeState,
    useCustomSwitchNames, switchName, isRelayButton
}) {
    let c = LampButton;
    if (isRelayButton)
        c = RelayButton;
    return (
        <div className={"border " + (darkerBorder ? "darker " : "lighter ") + (useCustomSwitchNames ? "named" : "")}>
            {React.createElement(c, {
                on, buttonOrder, canStateBeNoChange: true,
                onClickH: onClickHandler, showStates: true,
                switchName, useCustomSwitchNames, waitingForChangeState
            })}
        </div>
    )
}

export default function LampOrRelayControl({ device, darkerBorder, canStateBeNoChange }) {
    const s = useLocalizedStrings();
    const ds = s.deviceSettings;

    const onClickH = useCallback((key) => {
        return (event) => {
            event.preventDefault();
            let changeStateObject = {}

            if ((device.type !== DeviceType.lamp) && (device.type !== DeviceType.lampX1TwoWay) && canStateBeNoChange) { // only in scenario creation
                if (device[key] === false) {
                    changeStateObject[key] = true;
                } else if (device[key] === true) {
                    changeStateObject[key] = undefined;
                } else {
                    changeStateObject[key] = false;
                }
            } else {
                changeStateObject[key] = !device[key]
            }

            device.changeState(changeStateObject);
        }
    }, [device, canStateBeNoChange]);

    const containerClass = device.active ?
        "device-detail-control with-flex active" : "device-detail-control with-flex inactive";

    switch (device.type) {
        case DeviceType.lamp:
        case DeviceType.lampX1TwoWay:
            return (
                <div className={containerClass}>
                    <Button on={device.state} buttonOrder={1}
                        onClickHandler={onClickH("state")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch1_name !== undefined ?
                                device.switch1_name : ds.switchName1Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state"]} />
                </div>
            )
        case DeviceType.dpst:
            return (
                <div className={containerClass}>
                    <Button on={device.state1} buttonOrder={1}
                        onClickHandler={onClickH("state1")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch1_name !== undefined ?
                                device.switch1_name : ds.switchName1Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state1"]} />
                    <Button on={device.state2} buttonOrder={2}
                        onClickHandler={onClickH("state2")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch2_name !== undefined ?
                                device.switch2_name : ds.switchName2Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state2"]} />
                </div>
            )
        case DeviceType.lampX3:
            return (
                <div className={containerClass}>
                    <Button on={device.state1} buttonOrder={1}
                        onClickHandler={onClickH("state1")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch1_name !== undefined ?
                                device.switch1_name : ds.switchName1Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state1"]} />
                    <Button on={device.state2} buttonOrder={2}
                        onClickHandler={onClickH("state2")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch2_name !== undefined ?
                                device.switch2_name : ds.switchName2Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state2"]} />
                    <Button on={device.state3} buttonOrder={3}
                        onClickHandler={onClickH("state3")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch3_name !== undefined ?
                                device.switch3_name : ds.switchName3Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state3"]} />
                </div>
            )
        case DeviceType.lampX4:
            return (
                <div className={containerClass}>
                    <Button on={device.state1} buttonOrder={1}
                        onClickHandler={onClickH("state1")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch1_name !== undefined ?
                                device.switch1_name : ds.switchName1Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state1"]} />
                    <Button on={device.state2} buttonOrder={2}
                        onClickHandler={onClickH("state2")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch2_name !== undefined ?
                                device.switch2_name : ds.switchName2Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state2"]} />
                    <Button on={device.state3} buttonOrder={3}
                        onClickHandler={onClickH("state3")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch3_name !== undefined ?
                                device.switch3_name : ds.switchName3Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state3"]} />
                    <Button on={device.state4} buttonOrder={4}
                        onClickHandler={onClickH("state4")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.switch4_name !== undefined ?
                                device.switch4_name : ds.switchName4Default}
                        useCustomSwitchNames={device.use_custom_switch_names}
                        waitingForChangeState={device.waitingForChangeState["state4"]} />
                </div>
            )
        case DeviceType.relay4:
            return (
                <div className={containerClass}>
                    <Button on={device.channel1} buttonOrder={1}
                        onClickHandler={onClickH("channel1")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.channel1_name !== undefined ?
                                device.channel1_name : ds.channelName1Default}
                        useCustomSwitchNames={device.use_custom_channel_names}
                        waitingForChangeState={device.waitingForChangeState["channel1"]}
                        isRelayButton={true} />
                    <Button on={device.channel2} buttonOrder={2}
                        onClickHandler={onClickH("channel2")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.channel2_name !== undefined ?
                                device.channel2_name : ds.channelName2Default}
                        useCustomSwitchNames={device.use_custom_channel_names}
                        waitingForChangeState={device.waitingForChangeState["channel2"]}
                        isRelayButton={true} />
                    <Button on={device.channel3} buttonOrder={3}
                        onClickHandler={onClickH("channel3")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.channel3_name !== undefined ?
                                device.channel3_name : ds.channelName3Default}
                        useCustomSwitchNames={device.use_custom_channel_names}
                        waitingForChangeState={device.waitingForChangeState["channel3"]}
                        isRelayButton={true} />
                    <Button on={device.channel4} buttonOrder={4}
                        onClickHandler={onClickH("channel4")}
                        darkerBorder={darkerBorder}
                        switchName={
                            device.channel4_name !== undefined ?
                                device.channel4_name : ds.channelName4Default}
                        useCustomSwitchNames={device.use_custom_channel_names}
                        waitingForChangeState={device.waitingForChangeState["channel4"]}
                        isRelayButton={true} />
                </div>
            )
        default:
            return null;
    }
}

export function Curtain1Lamp2Control({ device, darkerBorder, canStateBeNoChange }) {
    const s = useLocalizedStrings();
    const ds = s.deviceSettings;

    const onClickHToggle = useCallback((key) => {
        return (event) => {
            event.preventDefault();
            let changeStateObject = {}
            if (canStateBeNoChange) { // only in scenario creation
                if (device[key] === false) {
                    changeStateObject[key] = true;
                } else if (device[key] === true) {
                    changeStateObject[key] = undefined;
                } else {
                    changeStateObject[key] = false;
                }
            } else {
                changeStateObject[key] = !device[key]
            }
            device.changeState(changeStateObject);
        }
    }, [device, canStateBeNoChange]);

    const onClickHV = useCallback((key, value) => {
        return (event) => {
            event.preventDefault();
            let changeStateObject = {}
            changeStateObject[key] = value
            device.changeState(changeStateObject);
        }
    }, [device]);

    const containerClass = device.active ?
        "device-detail-control with-flex curtain-active" : "device-detail-control with-flex curtain-inactive";

    return (
        <div className={containerClass}>
            <Button on={device.lamp1} buttonOrder={1}
                onClickHandler={onClickHToggle("lamp1")}
                darkerBorder={darkerBorder}
                switchName={
                    device.switch1_name !== undefined ?
                        device.switch1_name : ds.switchName1Default}
                useCustomSwitchNames={device.use_custom_switch_names}
                waitingForChangeState={device.waitingForChangeState["lamp1"]} />
            <Button on={device.lamp2} buttonOrder={2}
                onClickHandler={onClickHToggle("lamp2")}
                darkerBorder={darkerBorder}
                switchName={
                    device.switch2_name !== undefined ?
                        device.switch2_name : ds.switchName2Default}
                useCustomSwitchNames={device.use_custom_switch_names}
                waitingForChangeState={device.waitingForChangeState["lamp2"]} />

            <div className={"curtain-control-box compact " + (darkerBorder ? "darker " : "")}>
                <CurtainSingleButton channel={1} open={true} buttonOrder={1}
                    onClickH={onClickHV("curtain", CurtainState.open)}
                    showStates={true} />
                <CurtainSingleButton channel={1} open={false} buttonOrder={2}
                    onClickH={onClickHV("curtain", CurtainState.close)}
                    showStates={true} />

                <CurtainStatePanel
                    notShowInfoSection={true}
                    side="full" channel={1}
                    state={device.curtain}
                    onClickH={onClickHV("curtain", CurtainState.idle)}
                    waitingForChangeState={device.waitingForChangeState["curtain"]} />
            </div>
        </div>
    )
}